import React, { useEffect, useState, useRef, useCallback } from "react";
import Modal from "../base/Modal";
import AttachmentItem from "./AttachmentIcon";
import { FaPaperclip } from "react-icons/fa6";
import CalendarEventEditor from "../meeting/CalendarEventEditor";
import { AiOutlineCheck, AiOutlineClose } from "react-icons/ai";
import SunEditorComponent from "./SunEditorComponent";
import ReactMarkdown from "react-markdown";
import toast from "react-hot-toast";
import { FiAlertTriangle, FiUser, FiMinus, FiArrowDown } from "react-icons/fi";
// Import the utility functions for mapping calendar event data
import { mapEventData, unmapEventData } from "../../utils/eventMapper";

// --- Tool Definitions (Unchanged for scope) ---
const PREVIEW_TOOLS = {
    email: ["send_email_through_gmail", "create_draft_on_gmail", "send_message_in_thread_on_gmail", "reply_to-email_on_gmail", "send_email_outlook", "reply_to_outlook_email", "create_draft_email_outlook", "forward_outlook_email", "update_email_message_outlook", "delete_email_message_outlook","reply_to_email_on_gmail"],
    message: ["send_direct_message_to-user_microsoft_teams", "send_message_in_chat_microsoft_teams", "send_message_in_channel_microsoft_teams", "create_team_microsoft_teams", "create_channel_microsoft_teams", "add_member_to_team_microsoft_teams", "delete_channel_microsoft_teams",],
    confluence: ["create_page_confluence", "update_page_confluence", "delete_page_confluence", "create_space_confluence", "delete_space_confluence"],
    calendar: ["create_google_calendar_event", "create_calendar_event_outlook", "update_calendar_event", "delete_event_outlook"],
    jira: ["create_sprint_in_jira", "add_watchers_to_issue_in_jira", "transition_status_of_jira_issue", "move_issues_to_jira_backlog", "move_specific_issues_to_backlog_of-specific_board_in-JIRA", "move_issues-from-backlog-to-specific-JIRA-board", "move_issues-to-epic-in-jira", "jira_rank_epics", "jira_rank_issues", "update_the_estimation_of_an_issue_for_a_specified_board_in-jira", "jira_update_sprint", "move_issues-to-sprint-and-rank-in-jira", "swap_jira_sprint", "remove_issues-from-epic-in-jira", "add_attachment_to-specific-issue-in-JIRA", "delete_issue_in-jira", "update_jira_issue", "create_jira_issue", "add_comment_to-JIRA-issue", "update_comment_on_jira_issue"],
    github: ["create_github_repository_github", "update_github_repo_github", "transfer_repository_github", "create_branch_protection_github", "delete_branch_protection_github", "create_commit_github", "add_commit_comment_github", "create_pull_request_github", "update_pull_request_github", "merge_pull_request_github", "request_pull_request_review_github", "create_github_issue_github", "update_github_issue_github", "add_assignees_to-issue-github", "remove_assignees-from-issue_github", "create_label_github", "update_label_github", "delete_label_github", "create_milestone_github", "update_milestone_github", "create_release_github", "update_release_github", "create_fork_github", "merge_forks_github", "add_repository_collaborator_github", "create_repository_webhook_github", "update_repository_webhook_github"],
    outlookMisc: ["create_contact_outlook", "update_contact_outlook", "delete_contact_outlook", "update_user_profile_photo_outlook", "create_graph_subscription_outlook", "delete_graph_subscription_outlook", "add_commit_comment_outlook", "create_outlook_task", "update_outlook_task", "delete_outlook_task", "download_and_save_attachments_outlook"],
};

// --- Helper Functions (Mostly Unchanged, just imported here for context) ---
const getContentParamName = (funcName, parsedArguments) => {
    if (PREVIEW_TOOLS.email.includes(funcName)) {
        return funcName.includes("outlook") ? "body" : "html_message_body";
    }
    if (PREVIEW_TOOLS.message.includes(funcName)) {
        return parsedArguments.message ? "message" : "message_content";
    }
    if (PREVIEW_TOOLS.confluence.includes(funcName)) {
        return "content";
    }
    if (funcName === "create_jira_issue") {
        if (parsedArguments.fields_to_update?.description !== undefined) return "fields_to_update.description";
        if (parsedArguments.additional_fields?.description !== undefined) return "additional_fields.description";
    }
    if (funcName === "update_jira_issue") {
        if (parsedArguments.fields_to_update?.description !== undefined) return "fields_to_update.description";
    }
    if (funcName === "add_comment_to_JIRA_issue") {
        if (parsedArguments.body !== undefined) return "body";
    }
    if (funcName === "update_comment_on_jira_issue") {
        if (parsedArguments.comment_body !== undefined) return "comment_body";
    }
    return "";
};

const getContentValue = (paramName, parsedArguments) => {
    if (!paramName) return null;
    const parts = paramName.split('.');
    let value = parsedArguments;
    for (const part of parts) {
        if (value && typeof value === 'object' && part in value) {
            value = value[part];
        } else {
            return null;
        }
    }
    return value;
};

// Helper for badge styling with Conditional Logic (Repeated in renderContentPreview for Jira)
const getLabelStyle = (key, value) => {
    const lowerValue = String(value).toLowerCase();
    if (key === "Priority") {
        if (lowerValue.includes("high") || lowerValue.includes("urgent")) {
            return { Icon: FiAlertTriangle, bg: "bg-red-100", text: "text-red-700" };
        }
        if (lowerValue.includes("medium") || lowerValue.includes("normal")) {
            return { Icon: null, bg: "bg-yellow-100", text: "text-yellow-700" };
        }
        return { Icon: FiArrowDown, bg: "bg-gray-100", text: "text-gray-600" }; 
    }
    if (key === "Assignee") {
        return { Icon: FiUser, bg: "bg-gray-100", text: "text-gray-700" };
    }
    return { Icon: null, bg: "bg-blue-100", text: "text-blue-700" };
};


// --- CORRECTED: renderGenericArguments (Re-integrated Label/Badge logic for separate rendering) ---
const renderGenericArguments = (parsedArguments, contentParamName) => {
    const fieldMap = {
        project_identifier: "Project Identifier",
        project_key: "Project Key",
        issue_id_or_key: "Issue ID/Key",
        transition_id: "Transition ID",
        transition_name: "Transition Status",
        // Added for non-jira tools
        base_url: "Base URL",
        repository_name: "Repository Name",
        branch_name: "Branch Name",
        pull_number: "Pull Request #",
    };

    const mainFieldsToExclude = [
        "summary", "issue_type_name", "title", "to", "recipients", "subject",
    ];

    const extractedFields = {};
    const extractedLabels = {}; // To pass label data out
    const nestedFields = parsedArguments.fields_to_update || parsedArguments.additional_fields || {};
    
    // Keys that should be extracted as 'badges' / excluded from general fields list
    const badgeKeys = ["priority", "labels", "assignee", "parent", "description", "body"]; 

    // 1. Extract special fields for Labels/Badges (Logic remains the same for extraction)
    Object.entries(nestedFields).forEach(([key, value]) => {
        if (key === "priority" && typeof value === "object" && value.name) {
            extractedLabels["Priority"] = value.name;
        } else if (key === "labels" && Array.isArray(value) && value.length > 0) {
            extractedLabels["Labels"] = value;
        } else if (key === "assignee" && typeof value === "object") {
            let assigneeValue = value.displayName || value.name || value.emailAddress || "";
            if (!assigneeValue) {
                const actionDesc = parsedArguments.action_description || "";
                const nameMatch = actionDesc.match(/assigned to ([^,]+)/i) || actionDesc.match(/assignee: ([^,]+)/i);
                if (nameMatch) {
                    const potentialName = nameMatch[1].trim();
                    if (!potentialName.includes(":") && !potentialName.includes("-")) {
                        assigneeValue = potentialName;
                    }
                }
            }
            if (assigneeValue) {
                extractedLabels["Assignee"] = assigneeValue;
            }
        } else if (key === "parent" && typeof value === "object" && value.key) {
             extractedLabels["Epic/Parent"] = value.key; 
        }
    });

    // 2. Extract remaining fields for the compact list
    const allKeys = new Set([...Object.keys(parsedArguments), ...Object.keys(nestedFields)]);

    // --- NEW: Get the base-level key of the content parameter ---
    // e.g., "html_message_body" OR "fields_to_update" from "fields_to_update.description"
    const baseContentParam = contentParamName ? contentParamName.split('.')[0] : "";
    
    allKeys.forEach((key) => {
        const value = parsedArguments[key];

        // --- NEW: Exclude the main content parameter (or its parent object) ---
        // This stops "html_message_body" or "fields_to_update" from being listed
        if (key === baseContentParam) return;
        
        // Exclude main fields, nested objects, and other non-metadata keys
        if (mainFieldsToExclude.includes(key) || 
            key.startsWith("customfield_") ||
            key === "fields_to_update" ||
            key === "additional_fields" ||
            key === "action_description" ||
            key === "jql_query" ||
            badgeKeys.includes(key)
        ) return;

        if (fieldMap[key] && value !== undefined && value !== null) {
            extractedFields[fieldMap[key]] = String(value);
        } else if (typeof value === "object" && value !== null) {
            // Check nested fields directly inside this object (e.g., 'committer' details)
            Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                if (nestedKey.startsWith("customfield_") || nestedKey.toLowerCase() === 'body' || nestedKey.toLowerCase() === 'description') return;
                
                const fullKey = `${key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} ${nestedKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`;
                if (nestedValue !== undefined && nestedValue !== null && typeof nestedValue !== 'object') {
                    extractedFields[fullKey] = String(nestedValue);
                }
            });
        } else if (value !== undefined && value !== null) {
            extractedFields[
                key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
            ] = String(value);
        }
    });

    // Merge in remaining non-excluded nested field values
    Object.entries(nestedFields).forEach(([key, value]) => {
         // Exclude fields already handled as labels/badges or content
        if (badgeKeys.includes(key) || key.startsWith("customfield_")) return;
        
        if (value !== undefined && value !== null && typeof value !== 'object') {
            extractedFields[
                key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
            ] = String(value);
        }
    });


    if (Object.keys(extractedLabels).length === 0 && Object.keys(extractedFields).length === 0) return null;

    const fieldsArray = Object.entries(extractedFields);

    return (
        <div className="p-3 bg-white border border-gray-300 rounded-lg shadow-sm max-h-[400px] overflow-y-auto no-scrollbar">

          
            {/* START: Labels (Multi-chip list, needs space before fields) */}
            {extractedLabels.Labels && (
                <div className="mb-3">
                    <span className="block mb-1 text-[11px] font-semibold uppercase text-gray-700">Labels</span>
                    <div className="flex flex-wrap items-center gap-1">
                        {extractedLabels.Labels.map((label, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-purple-100 text-purple-800">
                                {label}
                            </span>
                        ))}
                    </div>
                    {fieldsArray.length > 0 && (
                        <hr className="my-3 border-gray-200" />
                    )}
                </div>
            )}
            {/* END: Labels */}

            {/* START: Compact Vertical List for remaining fields (Metadata List) */}
            {fieldsArray.length > 0 && (
                <div className="grid grid-cols-1 gap-y-2">
                    {fieldsArray.map(([key, value]) => (
                        <div
                            key={key}
                            className="flex min-w-0 text-[12px] leading-tight" 
                        >
                            {/* Key: font-semibold and text-gray-700 */}
                            <span className="flex-shrink-0 font-semibold text-gray-700 whitespace-nowrap"> 
                                {key}:
                            </span>
                            {/* Value: font-medium and text-gray-700 */}
                            <span className="flex-grow ml-1 overflow-hidden font-medium text-gray-700 break-words"> 
                                {value}
                            </span>
                        </div>
                    ))}
                </div>
            )}
            {/* END: Compact Vertical List */}
        </div>
    );
};
// --- END CORRECTED renderGenericArguments VERSION ---


// --- Read More/Less Component (Unchanged) ---
const ReadMoreToggle = ({ content, isExpanded, onToggle, maxLines = 6 }) => {
    const lines = content.split('\n').length;
    const needsTruncation = lines > maxLines;

    const maxHeightStyle = needsTruncation && !isExpanded
        ? { maxHeight: `${maxLines * 1.5}em`, overflow: 'hidden' }
        : {};

    return (
        <div className="relative">
            <div className="transition-all duration-300 ease-in-out" style={maxHeightStyle}>
                <ReactMarkdown className="markdown-preview-jira">
                    {String(content || "")}
                </ReactMarkdown>
            </div>
            {needsTruncation && (
                <div className="text-right">
                    <button
                        onClick={onToggle}
                        className="text-xs font-semibold text-blue-600 hover:text-blue-800 focus:outline-none"
                    >
                        {isExpanded ? 'View Less...' : 'View More...'}
                    </button>
                </div>
            )}
        </div>
    );
};
// --- END ReadMoreToggle COMPONENT ---


const ToolApprovalUI = ({
    toolCallApprovalList,
    handleApprove,
    handleUpdate,
    disapprovalReasons,
    handleReasonChange,
    handleDisapprove,
}) => {
    const [currentToolIdForFeedback, setCurrentToolIdForFeedback] = useState(null);
    const [editingToolId, setEditingToolId] = useState(null);
    const [editedEmailContent, setEditedEmailContent] = useState({});
    const [editedJiraContent, setEditedJiraContent] = useState({});
    const [iframeSrc, setIframeSrc] = useState({});
    const iframeRef = useRef({});
    const [editedCalendarEvents, setEditedCalendarEvents] = useState({});

    // --- NEW STATE: To manage which description is expanded ---
    const [expandedDescriptions, setExpandedDescriptions] = useState({});

    // Toggle handler for the "View More/Less" button
    const toggleDescriptionExpand = useCallback((toolId) => {
        setExpandedDescriptions(prev => ({
            ...prev,
            [toolId]: !prev[toolId]
        }));
    }, []);
    // --- END NEW STATE AND HANDLER ---


    useEffect(() => {
        const newCalendarEvents = {};
        toolCallApprovalList.forEach(toolCall => {
            const toolName = toolCall.function.name;
            if (PREVIEW_TOOLS.calendar.includes(toolName)) {
                try {
                    const argumentsParsed = JSON.parse(toolCall.function.arguments);
                    newCalendarEvents[toolCall.id] = mapEventData(argumentsParsed, toolName, true);
                } catch (e) {
                    console.error("Failed to map calendar event state from props:", e);
                }
            }
        });
        setEditedCalendarEvents(newCalendarEvents);
    }, [toolCallApprovalList]);


    const handleEditClick = (toolCall) => {
        setEditingToolId(toolCall.id);
        let parsedArguments = {};
        try {
            parsedArguments = JSON.parse(toolCall.function.arguments);
        } catch (e) {
            console.error("Invalid JSON in arguments:", e);
            return;
        }
        const func_name = toolCall.function.name;
        const contentParamName = getContentParamName(func_name, parsedArguments);
        const contentValue = getContentValue(contentParamName, parsedArguments);

        if (PREVIEW_TOOLS.jira.includes(func_name)) {
            setEditedJiraContent({
                ...parsedArguments,
                [contentParamName]: contentValue,
            });
        } else {
            setEditedEmailContent({
                ...parsedArguments,
                [contentParamName]: contentValue,
            });
        }
        // When editing starts, immediately expand the full content for editing
        if (contentParamName) {
            setExpandedDescriptions(prev => ({ ...prev, [toolCall.id]: true }));
        }
    };

    const handleSaveEdit = useCallback(
        (toolCall) => {
            const func_name = toolCall.function?.name;
            const originalArgs = JSON.parse(toolCall.function.arguments);
            const contentParamName = getContentParamName(func_name, originalArgs);

            let updatedArgs = JSON.parse(JSON.stringify(originalArgs));
            let contentToUpdate = PREVIEW_TOOLS.jira.includes(func_name)
                ? editedJiraContent
                : editedEmailContent;

            const updatedContent = contentToUpdate[contentParamName];

            if (contentParamName) {
                const parts = contentParamName.split('.');
                let currentLevel = updatedArgs;
                for (let i = 0; i < parts.length - 1; i++) {
                    if (!currentLevel[parts[i]]) {
                        currentLevel[parts[i]] = {};
                    }
                    currentLevel = currentLevel[parts[i]];
                }
                currentLevel[parts[parts.length - 1]] = updatedContent;
            }

            if (PREVIEW_TOOLS.message.includes(func_name)) {
                updatedArgs.content_type = "html";
            }

            const updatedToolCall = {
                ...toolCall,
                function: {
                    ...toolCall.function,
                    arguments: JSON.stringify(updatedArgs),
                },
            };
            handleUpdate(toolCall.id, updatedToolCall);
            setEditingToolId(null);
            toast.success("Changes saved. Ready for approval.");
        },
        [editedEmailContent, editedJiraContent, handleUpdate]
    );

    const handleCancelEdit = () => {
        setEditingToolId(null);
    };

    const handleApproveAndSend = (toolCall) => {
        const toolName = toolCall.function.name;
        let finalToolCall = toolCall;

        if (editingToolId === toolCall.id) {
            const originalArgs = JSON.parse(toolCall.function.arguments);
            const contentParamName = getContentParamName(toolName, originalArgs);

            if (contentParamName) {
                let updatedArgs = JSON.parse(JSON.stringify(originalArgs));
                const contentToUpdate = PREVIEW_TOOLS.jira.includes(toolName)
                    ? editedJiraContent
                    : editedEmailContent;
                const updatedContent = contentToUpdate[contentParamName];

                const parts = contentParamName.split('.');
                let currentLevel = updatedArgs;
                for (let i = 0; i < parts.length - 1; i++) {
                    if (!currentLevel[parts[i]]) currentLevel[parts[i]] = {};
                    currentLevel = currentLevel[parts[i]];
                }
                currentLevel[parts[parts.length - 1]] = updatedContent;

                finalToolCall = {
                    ...toolCall,
                    function: {
                        ...toolCall.function,
                        arguments: JSON.stringify(updatedArgs),
                    },
                };
            }
        }

        handleApprove(toolCall.id, finalToolCall);
        setEditingToolId(null);
    };

    const getPreviewTitle = (functionName) => {
        if (functionName === 'delete_event_outlook') return "Confirm Event Deletion";
        if (functionName === 'delete_issue_in_jira') return "Confirm Issue Deletion";
        if (PREVIEW_TOOLS.email.includes(functionName)) return "Email Preview";
        if (PREVIEW_TOOLS.message.includes(functionName)) return "Message Preview";
        if (PREVIEW_TOOLS.confluence.includes(functionName)) return "Confluence Preview";
        if (PREVIEW_TOOLS.calendar.includes(functionName)) return "Calendar Event Preview";
        if (PREVIEW_TOOLS.jira.includes(functionName)) return "Jira Action Preview";
        if (PREVIEW_TOOLS.github.includes(functionName)) return "GitHub Action Preview";
        if (PREVIEW_TOOLS.outlookMisc.includes(functionName)) return "Outlook Action Preview";
        return "Tool Call Preview";
    };

    const renderDefaultContent = useCallback((toolName, parsedArguments) => {
        if (parsedArguments && parsedArguments.action_description) {
            return (
                <div className="p-4 text-gray-800 border border-gray-200 bg-gray-50 rounded-xl">
                    <p className="font-sans text-base">
                        {parsedArguments.action_description}
                    </p>
                </div>
            );
        }

        const formattedToolName = toolName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return (
            <div className="p-4 text-gray-700 border border-gray-200 bg-gray-50 rounded-xl">
                <p className="block font-sans text-sm font-medium">
                    <strong>Action:</strong> {formattedToolName}
                </p>
                {renderGenericArguments(parsedArguments, "")} 
            </div>
        );
    }, []);

    // --- MODIFIED RENDER CONTENT PREVIEW FUNCTION (Optimized Badge rendering for Jira layout) ---
    const renderContentPreview = useCallback((toolCall, parsedArguments) => {
        const {
            subject, to, recipients, file_paths = [], title, summary, issue_type_name
        } = parsedArguments;

        const isEditing = editingToolId === toolCall.id;
        const funcName = toolCall.function.name;

        const contentParamName = getContentParamName(funcName, parsedArguments);
        const contentValue = getContentValue(contentParamName, parsedArguments);

        const isEmailOrMsgOrConfluenceTool =
            [...PREVIEW_TOOLS.email, ...PREVIEW_TOOLS.message, ...PREVIEW_TOOLS.confluence].includes(funcName) &&
            !!contentParamName;

        const isJiraTool = PREVIEW_TOOLS.jira.includes(funcName); 

        let contentLabel = 'Content';
        if (isEmailOrMsgOrConfluenceTool) {
            contentLabel = funcName.includes('email') ? 'Email Body' : funcName.includes('message') ? 'Message Body' : 'Page Content';
        } else if (isJiraTool) {
            contentLabel = contentParamName.includes('description') ? 'Issue Description' : contentParamName.includes('body') ? 'Comment Body' : 'Content';
        }

        // --- Badge/Label Extraction Logic (Simplified) ---
        const nestedFields = parsedArguments.fields_to_update || parsedArguments.additional_fields || {};
        const extractedLabels = {};

        // Extract required badges for the JIRA header only
        if (isJiraTool) {
            if (nestedFields.priority && typeof nestedFields.priority === "object" && nestedFields.priority.name) {
                extractedLabels["Priority"] = nestedFields.priority.name;
            }
        }
        
        // The rest of the badge logic is now contained within renderGenericArguments for the side panel.


        return (
            <>
                {isJiraTool ? (
                    // Main JIRA Container with vertical separation
                    <div className="flex flex-col divide-x divide-gray-300 md:flex-row">
                        {/* Left/Center Section for Summary and Description (WIDER: 3/4) */}
                        <div className="flex-grow pr-4 md:w-3/4">
                            <div className="pb-1 mb-1 border-b border-gray-200">
                                
                                {/* --- START: TOP BADGE ROW (Issue Type, Priority) --- */}
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                    {/* Issue Type: */}
                                    {issue_type_name && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {issue_type_name}
                                        </span>
                                    )}
                                    {/* Priority: */}
                                    {extractedLabels.Priority && (() => {
                                        const { Icon, bg, text } = getLabelStyle("Priority", extractedLabels.Priority);
                                        return (
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}>
                                                {Icon && <Icon className="w-3.5 h-3.5 mr-1.5" />} 
                                                {extractedLabels.Priority}
                                            </span>
                                        );
                                    })()}
                                </div>
                                {/* --- END: TOP BADGE ROW --- */}
                                
                                {/* Summary - Now larger and centered */}
                                {summary && (
                                    <p className="block mt-2 font-sans text-lg font-bold text-gray-900">
                                        {summary}
                                    </p>
                                )}
                            </div>

                            {/* Main Content Body Header */}
                            {!!contentParamName && (
                                <p className="block mt-1 mb-2 font-sans text-sm font-medium text-gray-900 opacity-75">
                                    <strong>{contentLabel}:</strong>
                                </p>
                            )}

                            {/* Description/Comment Content */}
                            {!!contentParamName && (
                                <div className="relative w-full p-2 text-gray-800 bg-white border border-gray-300 shadow-md rounded-xl max-w-none">
                                    {!isEditing ? (
                                        // --- Use the new ReadMoreToggle component ---
                                        <ReadMoreToggle
                                            content={String(contentValue || "")}
                                            isExpanded={expandedDescriptions[toolCall.id]}
                                            onToggle={() => toggleDescriptionExpand(toolCall.id)}
                                            maxLines={6} // Set max lines before truncation
                                        />
                                    ) : (
                                        // Jira Content Editor
                                        <textarea
                                            className="w-full h-56 p-2 rounded-md no-scrollbar focus:outline-none min-h-[150px] max-h-[400px] border border-gray-300"
                                            value={editedJiraContent && editedJiraContent[contentParamName] !== undefined ? editedJiraContent[contentParamName] : ""}
                                            onChange={(e) =>
                                                setEditedJiraContent((prev) => ({ ...prev, [contentParamName]: e.target.value }))
                                            }
                                        />
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Right Section for Generic Arguments (NARROWER: 1/4) */}
                        <div className="flex-shrink-0 pl-4 md:w-1/4">
                            {renderGenericArguments(parsedArguments, contentParamName)}
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Original logic for Email/Message/Confluence/Other tools */}

                        {/* Email/Message/Confluence simple fields */}
                        {isEmailOrMsgOrConfluenceTool && (
                            <div className="mb-4">
                                {(to || recipients) && <p className="block font-sans text-sm font-medium opacity-75"><strong>Recipients:</strong> {(to || recipients)?.join(", ")}</p>}
                                {subject && <p className="block font-sans text-sm font-medium opacity-75"><strong>Subject:</strong> {subject}</p>}
                                {title && <p className="block font-sans text-sm font-medium opacity-75"><strong>Title:</strong> {title}</p>}
                            </div>
                        )}

                        {/* Generic Arguments for non-JIRA tools - HIDDEN FOR EMAILS */}
                        {!PREVIEW_TOOLS.email.includes(funcName) && renderGenericArguments(parsedArguments, contentParamName)}

                        {/* Main Content Body Header */}
                        {isEmailOrMsgOrConfluenceTool && (
                            <p className="block mt-4 mb-2 font-sans text-sm font-medium opacity-75">
                                <strong>{contentLabel}:</strong>
                            </p>
                        )}

                        {isEmailOrMsgOrConfluenceTool ? (
                            <>
                                {!isEditing ? (
                                    <div className="relative w-full p-2 mt-2 text-gray-800 bg-white border border-gray-300 shadow-md rounded-xl">
                                        <iframe
                                            ref={(el) => (iframeRef.current[toolCall.id] = el)}
                                            title="Content Preview"
                                            src={iframeSrc[toolCall.id]}
                                            style={{ width: "100%", border: "none" }}
                                            sandbox="allow-scripts allow-same-origin"
                                        />
                                    </div>
                                ) : (
                                    <div className="relative w-full p-2 mt-2 bg-white border border-gray-300 shadow-md rounded-xl no-scrollbar">
                                        <SunEditorComponent
                                            value={getContentValue(contentParamName, editedEmailContent)}
                                            onChange={(content) =>
                                                setEditedEmailContent((prev) => ({ ...prev, [contentParamName]: content }))
                                            }
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            renderDefaultContent(funcName, parsedArguments)
                        )}
                    </>
                )}

                {file_paths.length > 0 && (
                    <>
                        <div className="flex items-center gap-2 pb-2 mt-4 font-sans text-sm font-medium border-b border-gray-300 opacity-75 ">
                            <FaPaperclip className="w-4 h-4" /> Attachments
                        </div>
                        <div className="flex flex-wrap items-center flex-grow gap-2 p-1 mt-2">
                            {file_paths.map((filePath, index) => {
                                const fileName = filePath.split("/").pop();
                                return <AttachmentItem key={index} file={{ name: fileName }} />;
                            })}
                        </div>
                    </>
                )}
            </>
        );
    }, [editingToolId, editedEmailContent, editedJiraContent, iframeSrc, renderDefaultContent, expandedDescriptions, toggleDescriptionExpand]);
    // --- END MODIFIED RENDER CONTENT PREVIEW FUNCTION ---

    useEffect(() => {
        const newIframeSrcs = {};
        const blobUrlsToRevoke = [];
        const functionsToPreview = [...PREVIEW_TOOLS.email, ...PREVIEW_TOOLS.message, ...PREVIEW_TOOLS.confluence];

        toolCallApprovalList.forEach((toolCall) => {
            if (toolCall.approvalNeeded && !toolCall.approved) {
                const parsedArguments = JSON.parse(toolCall.function?.arguments || "{}");
                const funcName = toolCall.function.name;

                if (functionsToPreview.includes(funcName)) {
                    const contentParamName = getContentParamName(funcName, parsedArguments);
                    const htmlContentBody = getContentValue(contentParamName, parsedArguments);

                    if (htmlContentBody) {
                        const fullHtml = `<!DOCTYPE html><html lang="en"><head><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>body { margin: 0; padding: 1rem; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #1a202c; font-size: 14px; line-height: 1.5; background-color: transparent; word-wrap: break-word; } * { box-sizing: border-box; max-width: 100%; } img, iframe, video { max-width: 100%; height: auto; display: block; } table {width: 100% !important;border-collapse: collapse;table-layout: auto;font-family: Arial, sans-serif;border-radius: 8px;overflow: hidden;box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);} th,td { word-break: break-word; border: 1px solid #171717ff; padding: 12px 15px;text-align: left; } th { background-color: #f5f5f5; color: #555; font-weight: bold; } tr:nth-child(even) {background-color: #fafafa;} p { margin: 0.5rem 0; } ::-webkit-scrollbar { display: none; } body { -ms-overflow-style: none; scrollbar-width: none; } </style></head><body>${htmlContentBody}</body></html>`;
                        const blob = new Blob([fullHtml], { type: "text/html" });
                        const url = URL.createObjectURL(blob);
                        newIframeSrcs[toolCall.id] = url;
                        blobUrlsToRevoke.push(url);
                    }
                }
            }
        });

        setIframeSrc(newIframeSrcs);

        return () => {
            blobUrlsToRevoke.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [toolCallApprovalList, editingToolId]);

    useEffect(() => {
        toolCallApprovalList.forEach((toolCall) => {
            const iframe = iframeRef.current[toolCall.id];
            if (iframe && iframe.contentWindow) {
                iframe.onload = () => {
                    try {
                        const contentHeight = iframe.contentWindow.document.body.scrollHeight;
                        const newHeight = Math.min(Math.max(contentHeight, 200), 600);
                        iframe.style.height = `${newHeight}px`;
                        iframe.style.overflowY = contentHeight > 600 ? "auto" : "hidden";
                    } catch (e) {
                        console.error("Could not access iframe content:", e);
                    }
                };
            }
        });
    }, [iframeSrc, toolCallApprovalList]);

    if (toolCallApprovalList.length === 0) {
        return null;
    }

    return (
        <>
            <style>{`
                .markdown-preview-jira {
                    font-size: 0.8rem;
                    line-height: 1.4;
                    color: #222;
                }

                .markdown-preview-jira p,
                .markdown-preview-jira ul,
                .markdown-preview-jira ol,
                .markdown-preview-jira h1,
                .markdown-preview-jira h2,
                .markdown-preview-jira h3 {
                    margin-top: 0.1rem;
                    margin-bottom: 0.1rem;
                }

                .markdown-preview-jira pre {
                    margin: 0.5rem 0;
                    background-color: #f6f6f6;
                    padding: 0.6rem;
                    border-radius: 6px;
                    overflow-x: auto;
                    font-size: 0.75rem;
                }

                .markdown-preview-jira ul,
                .markdown-preview-jira ol {
                    margin-left: 16px;
                    padding-left: 0;
                }

                .markdown-preview-jira ul {
                    list-style-type: disc;
                }

                .markdown-preview-jira ol {
                    list-style-type: decimal;
                }

                .markdown-preview-jira h1,
                .markdown-preview-jira h2,
                .markdown-preview-jira h3 {
                    font-weight: 600;
                    color: #111;
                }

                .markdown-preview-jira h1 { font-size: 1em; }
                .markdown-preview-jira h2 { font-size: 0.95em; }
                .markdown-preview-jira h3 { font-size: 0.9em; }

                .markdown-preview-jira code {
                    background-color: #f2f2f2;
                    padding: 1px 3px;
                    border-radius: 3px;
                    font-family: monospace;
                    font-size: 0.75rem;
                }

                /* --- NEW: Custom no-scrollbar utility class for Tailwind-style scrollable containers --- */
                .no-scrollbar::-webkit-scrollbar {
                    display: none;
                }

                .no-scrollbar {
                    -ms-overflow-style: none;  /* IE and Edge */
                    scrollbar-width: none;  /* Firefox */
                }
                /* --- END NEW --- */
            `}</style>

            <div className="flex flex-col items-center justify-center space-y-6 tool-requests">
                {toolCallApprovalList
                    .filter((toolCall) => toolCall.approvalNeeded && !toolCall.approved)
                    .map((toolCall) => {
                        let argumentsParsed = {};
                        try {
                            argumentsParsed = JSON.parse(toolCall.function?.arguments);
                        } catch (e) {
                            console.error("Invalid JSON in arguments:", e);
                        }
                        const toolName = toolCall.function.name;
                        const isCalendarTool = PREVIEW_TOOLS.calendar.includes(toolName);
                        const isEditing = editingToolId === toolCall.id;
                        const contentParamName = getContentParamName(toolName, argumentsParsed);
                        const isEditableContentTool = !!contentParamName;

                        const isDeleteTool =
                            toolName === 'delete_event_outlook' ||
                            toolName === 'delete_issue_in_jira';

                        return (
                            <div key={toolCall.id} data-tool-id={toolCall.id} className="relative flex flex-col w-full gap-2 mb-8 text-gray-700 bg-white shadow-md tool-request rounded-xl">
                                <div className="p-6 pb-2">
                                    <p className="block mb-2 font-sans text-lg font-medium leading-relaxed text-blue-gray-200">
                                        <strong>{getPreviewTitle(toolName)}</strong>
                                    </p>

                                    {isDeleteTool ? (
                                        <div className="py-4 text-center">

                                            <div className="inline-block w-full max-w-md p-3 font-semibold text-red-700 bg-red-100 border border-red-200 rounded-lg text-md">
                                                Are you sure you want to permanently delete this item?
                                            </div>
                                            <p className="mt-3 text-sm text-gray-500">This action cannot be undone.</p>
                                        </div>
                                    ) : isEditableContentTool || PREVIEW_TOOLS.jira.includes(toolName) ? (
                                        renderContentPreview(toolCall, argumentsParsed)
                                    ) : isCalendarTool ? (
                                        <CalendarEventEditor
                                            event={editedCalendarEvents[toolCall.id]}
                                            onUpdate={(updatedEvent) => {
                                                setEditedCalendarEvents(prev => ({
                                                    ...prev,
                                                    [toolCall.id]: updatedEvent,
                                                }));
                                                const toolName = toolCall.function.name;
                                                const originalArgs = JSON.parse(toolCall.function.arguments);
                                                const finalUpdatedArgs = unmapEventData(updatedEvent, toolName, originalArgs);
                                                const updatedToolCall = {
                                                    ...toolCall,
                                                    function: {
                                                        ...toolCall.function,
                                                        arguments: JSON.stringify(finalUpdatedArgs),
                                                    },
                                                };
                                                handleUpdate(toolCall.id, updatedToolCall);
                                            }}
                                            functionName={toolName}
                                            approvalNeeded={!isDeleteTool}
                                        />
                                    ) : (
                                        renderDefaultContent(toolName, argumentsParsed)
                                    )}
                                </div>
                                <div className="flex items-center justify-end gap-2 p-6 pt-0 text-white">
                                    {isEditing ? (
                                        <>
                                            <button className="px-4 py-1 text-xs transition-transform duration-200 ease-in-out transform border rounded-lg bg-zinc-800 border-zinc-700 hover:scale-105" onClick={() => handleSaveEdit(toolCall)}>
                                                <AiOutlineCheck className="inline-block mr-1" /> Save
                                            </button>
                                            <button className="px-4 py-1 text-xs transition-transform duration-200 ease-in-out transform border rounded-lg bg-zinc-800 border-zinc-700 hover:scale-105" onClick={handleCancelEdit}>
                                                <AiOutlineClose className="inline-block mr-1" /> Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            {isEditableContentTool && (
                                                <button className="px-4 py-1 text-xs transition-transform duration-200 ease-in-out transform border rounded-lg bg-zinc-800 border-zinc-700 hover:scale-105" onClick={() => handleEditClick(toolCall)}>
                                                    Edit
                                                </button>
                                            )}
                                            <button className="px-4 py-1 text-xs transition-transform duration-200 ease-in-out transform border rounded-lg bg-zinc-800 border-zinc-700 hover:scale-105" onClick={() => setCurrentToolIdForFeedback(toolCall.id)}>
                                                Add Feedback
                                            </button>
                                            <button className="px-4 py-1 text-xs transition-transform duration-200 ease-in-out transform border rounded-lg bg-zinc-800 border-zinc-700 hover:scale-105" onClick={() => handleApproveAndSend(toolCall)}>
                                                <AiOutlineCheck className="inline-block mr-1" /> Approve
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
            </div>
            {currentToolIdForFeedback && (
                <Modal title="Add Feedback" isOpen={true} onClose={() => setCurrentToolIdForFeedback(null)}>
                    <div className="p-8">
                        <textarea
                            placeholder="Provide feedback or reasons for disapproval..."
                            className="w-full px-3 py-2 transition duration-200 border rounded-md resize-none bg-zinc-800 border-zinc-700 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#5652CF] focus:border-transparent"
                            value={disapprovalReasons[currentToolIdForFeedback] || ""}
                            onChange={(e) => handleReasonChange(currentToolIdForFeedback, e.target.value)}
                        />
                        <div className="flex justify-end mt-4">
                            <button
                                className="px-4 py-1 text-xs transition-transform duration-200 ease-in-out transform border rounded-lg bg-zinc-800 border-zinc-700 hover:scale-105"
                                onClick={() => {
                                    handleDisapprove(currentToolIdForFeedback);
                                    setCurrentToolIdForFeedback(null);
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default ToolApprovalUI;